/**
 * Authentication error catalog (ADR-015 §5, §10) and security event types (ADR-015 §9). The API
 * answers with these codes; the apps show the user-facing message. Messages never reveal whether an
 * account exists: the account state is only told after a valid credential. No zod here so web
 * bundles that only need messages do not pull the schema library.
 */
export const AUTH_ERROR_CODES = [
  'INVALID_CREDENTIALS',
  'ACCOUNT_SUSPENDED',
  'ACCOUNT_DISABLED',
  'TENANT_SUSPENDED',
  'APP_NOT_ALLOWED',
  'MFA_REQUIRED',
  'MFA_SETUP_REQUIRED',
  'MFA_INVALID',
  'CHALLENGE_EXPIRED',
  'INVITATION_INVALID',
  'INVITATION_EXPIRED',
  'INVITATION_USED',
  'OTP_INVALID',
  'OTP_EXPIRED',
  'OTP_TOO_MANY_ATTEMPTS',
  'RATE_LIMITED',
  'AUTH_UNAVAILABLE',
  'SESSION_EXPIRED',
  'SESSION_REVOKED',
  'REAUTH_REQUIRED',
  'EXTERNAL_LOGIN_FAILED',
  'EXTERNAL_ALREADY_LINKED',
  'EXTERNAL_NOT_LINKED',
  'FORBIDDEN',
  // added with the E1 security review (SEC-E1-*): codes the API already answered, now in the catalog
  'MFA_ENROLLMENT_BLOCKED',
  'CSRF',
  'INVALID_PASSWORD',
  'MFA_ENROLL_BLOCKED',
  'PHONE_TAKEN',
  'EMAIL_TAKEN',
  'INVALID_STATUS_CHANGE',
  'LAST_OWNER',
  'WEAK_PASSWORD',
  'UNAUTHORIZED',
  'PHONE_REQUIRES_UPDATE',
  // added with E2 (invitations, activation, phone change)
  'ACTIVATION_EXPIRED',
  'MFA_RESET_TOO_SOON',
  // added with the E2 security review (SEC-E2-1, SEC-E2-3)
  'SECOND_ADMIN_REQUIRED',
  'MFA_INVITATION_TOO_SOON',
  // added with E3 (recovery, password change, Google, second factor enrollment with a code)
  'RECOVERY_EXPIRED',
  'PHONE_NOT_VERIFIED',
  'EXTERNAL_TICKET_EXPIRED',
  'EXTERNAL_LINK_EXISTS',
  'PASSWORD_REQUIRED',
  // added with E4 (catalog check: codes the API already answered)
  'MFA_ALREADY_ENABLED',
  'OPERATOR_EXISTS',
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

export interface AuthErrorInfo {
  /** For logs and developers; never shown to the person. */
  technical: string;
  /** Shown as is by the apps. */
  user: string;
}

export const AUTH_ERRORS: Record<AuthErrorCode, AuthErrorInfo> = {
  INVALID_CREDENTIALS: {
    technical: 'unknown identifier, wrong password or account without that method (indistinguishable on purpose)',
    user: 'Los datos de ingreso no son correctos.',
  },
  ACCOUNT_SUSPENDED: {
    technical: 'users.status = SUSPENDED (only told after a valid credential)',
    user: 'Tu cuenta está suspendida. Comunícate con el administrador de tu empresa.',
  },
  ACCOUNT_DISABLED: {
    technical: 'users.status = DISABLED (only told after a valid credential)',
    user: 'Tu cuenta está desactivada. Comunícate con el administrador de tu empresa.',
  },
  TENANT_SUSPENDED: { technical: 'tenants.status is not ACTIVE', user: 'El acceso de tu empresa está suspendido. Comunícate con el administrador.' },
  APP_NOT_ALLOWED: {
    technical: 'the account lacks the permissions of the audience served by this host',
    user: 'Tu cuenta no tiene acceso a esta aplicación.',
  },
  MFA_REQUIRED: { technical: 'sign-in challenge pending: verify the second factor', user: 'Ingresa el código de tu aplicación de autenticación.' },
  MFA_SETUP_REQUIRED: {
    technical: 'the policy demands a second factor and none is confirmed',
    user: 'Tu cuenta necesita verificación en dos pasos. Configúrala para continuar.',
  },
  MFA_INVALID: { technical: 'wrong, expired or already used TOTP / recovery code', user: 'El código no es correcto. Inténtalo de nuevo.' },
  CHALLENGE_EXPIRED: { technical: 'sign-in challenge expired, consumed or bound to another browser', user: 'El ingreso tardó demasiado. Empieza de nuevo.' },
  INVITATION_INVALID: {
    technical: 'unknown, tampered or revoked invitation token',
    user: 'Este enlace de invitación no es válido. Pide uno nuevo al administrador.',
  },
  INVITATION_EXPIRED: { technical: 'invitation past expires_at', user: 'Este enlace de invitación venció. Pide uno nuevo al administrador.' },
  INVITATION_USED: { technical: 'invitation already ACCEPTED', user: 'Este enlace ya se usó. Ingresa con tu celular o correo.' },
  OTP_INVALID: { technical: 'wrong OTP code for the challenge', user: 'El código no es correcto. Revísalo e inténtalo de nuevo.' },
  OTP_EXPIRED: { technical: 'OTP challenge expired, replaced by a newer one or already used', user: 'El código venció. Pide uno nuevo.' },
  OTP_TOO_MANY_ATTEMPTS: { technical: 'OTP challenge reached its attempt limit', user: 'Demasiados intentos con este código. Pide uno nuevo.' },
  RATE_LIMITED: { technical: 'rate limit exceeded for the endpoint', user: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.' },
  AUTH_UNAVAILABLE: {
    technical: 'throttle store (Redis) unavailable: credential endpoints fail closed',
    user: 'El ingreso no está disponible en este momento. Inténtalo de nuevo en unos minutos.',
  },
  SESSION_EXPIRED: { technical: 'idle or maximum lifetime reached, or refresh token missing', user: 'Tu sesión terminó. Ingresa de nuevo.' },
  SESSION_REVOKED: {
    technical: 'session or family revoked (logout, admin, suspension, refresh reuse, credential change)',
    user: 'Tu sesión se cerró. Ingresa de nuevo.',
  },
  REAUTH_REQUIRED: { technical: 'operation needs a recent authentication', user: 'Confirma tu identidad para continuar.' },
  EXTERNAL_LOGIN_FAILED: {
    technical: 'OIDC flow failed (state, PKCE, callback, token or id_token validation)',
    user: 'No se pudo ingresar con Google. Inténtalo de nuevo.',
  },
  EXTERNAL_ALREADY_LINKED: {
    technical: 'the Google subject is linked to another account of the tenant',
    user: 'Esa cuenta de Google ya está vinculada a otro usuario.',
  },
  EXTERNAL_NOT_LINKED: {
    technical: 'no account of the tenant is linked to that Google subject',
    user: 'Esa cuenta de Google no está vinculada. Ingresa con tu celular o correo y vincúlala desde Mi cuenta.',
  },
  FORBIDDEN: { technical: 'missing permission or canManage denial', user: 'No tienes permiso para hacer esto.' },
  MFA_ENROLLMENT_BLOCKED: {
    technical: 'the policy demands a second factor, none is confirmed, and a first factor cannot be enrolled with the password alone (only inside an invitation or with an OTP)',
    user: 'Tu cuenta requiere verificación en dos pasos. Pide al administrador que te envíe una nueva invitación para configurarla.',
  },
  CSRF: {
    technical: 'cookie endpoint without X-Requested-With or from an Origin outside the allowlist',
    user: 'La solicitud no se pudo verificar. Recarga la página e inténtalo de nuevo.',
  },
  INVALID_PASSWORD: { technical: 'current password wrong during re-authentication or password change', user: 'La contraseña no es correcta.' },
  MFA_ENROLL_BLOCKED: {
    technical: 'second factor enrollment within 72 h after a password recovery (SEC-D-5)',
    user: 'Recuperaste tu contraseña hace poco. Podrás configurar la verificación en dos pasos 72 horas después.',
  },
  PHONE_TAKEN: { technical: 'users_tenant_phone_key: another account of the tenant has that phone', user: 'Ya existe un usuario con ese celular.' },
  EMAIL_TAKEN: { technical: 'unique (tenant_id, email): another account of the tenant has that email', user: 'Ya existe un usuario con ese correo.' },
  INVALID_STATUS_CHANGE: { technical: 'transition not allowed by the account state machine', user: 'La cuenta no admite ese cambio en su estado actual.' },
  LAST_OWNER: { technical: 'the operation would leave the tenant without an active owner', user: 'Debe existir al menos un propietario activo.' },
  WEAK_PASSWORD: {
    technical: 'password rejected by the policy (length, context words, common or breached)',
    user: 'La contraseña no es segura. Usa al menos 10 caracteres y evita datos personales o claves comunes.',
  },
  UNAUTHORIZED: { technical: 'missing, invalid or expired access token', user: 'Tu sesión no es válida. Ingresa de nuevo.' },
  PHONE_REQUIRES_UPDATE: {
    technical: 'the account has a placeholder phone (+57 399 range assigned by migration 0026): no invitation or code can be sent until it is corrected',
    user: 'El celular de esta cuenta es provisional. Actualízalo con el número real antes de enviar invitaciones o códigos.',
  },
  ACTIVATION_EXPIRED: {
    technical: 'activation ticket unknown, expired (15 min), already used or replaced by a newer code',
    user: 'La verificación venció. Abre de nuevo el enlace de invitación y pide otro código.',
  },
  MFA_RESET_TOO_SOON: {
    technical: 'second factor reset within 24 h after a phone change by the same administrator (SEC-D-2); details.retryAt',
    user: 'Cambiaste el número de esta cuenta hace poco. Otra persona administradora debe restablecer la verificación en dos pasos, o espera 24 horas.',
  },
  SECOND_ADMIN_REQUIRED: {
    technical:
      'phone change + second factor reset of one account by the same administrator (SEC-E2-1): always refused for an owner or an account with administrative permissions; for the rest, a phone change within 24 h of the reset (details.retryAt)',
    user: 'Por seguridad, este cambio debe hacerlo otro administrador.',
  },
  MFA_INVITATION_TOO_SOON: {
    technical: 'invitation to enroll the second factor of an ACTIVE account within 72 h after its password recovery (SEC-E2-3); details.retryAt',
    user: 'Esta cuenta recuperó su contraseña hace poco. Podrás enviarle la invitación para configurar la verificación en dos pasos 72 horas después de la recuperación.',
  },
  RECOVERY_EXPIRED: {
    technical: 'recovery grant unknown, expired (10 min), already used, or the account can no longer recover',
    user: 'La verificación venció. Pide un código nuevo para recuperar tu contraseña.',
  },
  PHONE_NOT_VERIFIED: {
    technical: 'the operation needs a code sent to a verified phone and users.phone_verified_at is null',
    user: 'Tu celular no está verificado. Pide al administrador una nueva invitación.',
  },
  EXTERNAL_TICKET_EXPIRED: {
    technical: 'Google link / re-authentication ticket unknown, expired (5 min), used, from another browser or for another account',
    user: 'La confirmación con Google venció. Inténtalo de nuevo.',
  },
  EXTERNAL_LINK_EXISTS: {
    technical: 'the account already has a Google identity: it must be unlinked before linking another one',
    user: 'Tu cuenta ya tiene una cuenta de Google vinculada. Desvincúlala primero.',
  },
  PASSWORD_REQUIRED: {
    technical: 'the Google identity cannot be removed from an account without a PASSWORD identity (it would be left with no way in)',
    user: 'Crea una contraseña antes de desvincular tu cuenta de Google.',
  },
  MFA_ALREADY_ENABLED: {
    technical: 'a second factor is already confirmed: a new one is only enrolled after disabling it',
    user: 'La verificación en dos pasos ya está activa. Desactívala primero para cambiar de teléfono.',
  },
  OPERATOR_EXISTS: { technical: 'platform_operators: another operator has that email', user: 'Ya existe un operador con ese correo.' },
};

export function isAuthErrorCode(code: unknown): code is AuthErrorCode {
  return typeof code === 'string' && (AUTH_ERROR_CODES as readonly string[]).includes(code);
}

export function authErrorText(code: AuthErrorCode | string | undefined): string {
  return isAuthErrorCode(code) ? AUTH_ERRORS[code].user : 'No se pudo completar la operación. Inténtalo de nuevo.';
}

/**
 * Security events recorded with `AuditService.record` (`audit_logs.action`), with the label shown in
 * the events view. Platform events (`platform.*`) live in `platform_audit_logs` and are not listed here.
 */
export const SECURITY_EVENTS = {
  'auth.login': 'Ingreso',
  'auth.login_failed': 'Ingreso fallido',
  'auth.logout': 'Cierre de sesión',
  'auth.logout_all': 'Cierre de todas las sesiones',
  'auth.session_revoked': 'Sesión revocada',
  'auth.refresh_reuse_detected': 'Reutilización de sesión detectada',
  'auth.throttle_blocked': 'Ingreso bloqueado por demasiados intentos',
  'auth.password_reset_requested': 'Recuperación de contraseña solicitada',
  'auth.password_reset_completed': 'Recuperación de contraseña completada',
  'auth.password_changed': 'Contraseña cambiada',
  'auth.reauth': 'Identidad confirmada',
  'auth.external_linked': 'Cuenta de Google vinculada',
  'auth.external_unlinked': 'Cuenta de Google desvinculada',
  'auth.external_login_failed': 'Retorno de Google rechazado',
  'auth.mfa_enabled': 'Verificación en dos pasos activada',
  'auth.mfa_disabled': 'Verificación en dos pasos desactivada',
  'auth.mfa_reset': 'Verificación en dos pasos restablecida',
  'auth.mfa_setup_blocked': 'Configuración de verificación en dos pasos bloqueada (requiere invitación)',
  'auth.otp_sent': 'Código de verificación enviado',
  'auth.otp_failed': 'Código de verificación incorrecto',
  // added with E4 (catalog check: events the API already recorded)
  'auth.mfa_failed': 'Verificación en dos pasos fallida',
  'auth.reauth_failed': 'Confirmación de identidad fallida',
  'auth.password_change_failed': 'Cambio de contraseña rechazado',
  'auth.recovery_code_used': 'Código de respaldo usado',
  'auth.recovery_codes_regenerated': 'Códigos de respaldo generados de nuevo',
  'user.dev_activated': 'Cuenta activada por la semilla de desarrollo',
  'user.invitation_created': 'Invitación creada',
  'user.invitation_resent': 'Invitación reenviada',
  'user.invitation_cancelled': 'Invitación cancelada',
  'user.invitation_accepted': 'Invitación aceptada',
  'user.suspended': 'Usuario suspendido',
  'user.reactivated': 'Usuario reactivado',
  'user.disabled': 'Usuario desactivado',
  'user.roles_changed': 'Roles cambiados',
  'user.phone_changed': 'Número de celular cambiado',
  'security_policy.changed': 'Política de seguridad cambiada',
} as const;

export type SecurityEventType = keyof typeof SECURITY_EVENTS;
export const SECURITY_EVENT_TYPES = Object.keys(SECURITY_EVENTS) as SecurityEventType[];

/** Events of the platform operators (`platform_audit_logs.action`), with their label. */
export const PLATFORM_SECURITY_EVENTS = {
  'platform.login': 'Ingreso de operador',
  'platform.login_failed': 'Ingreso de operador fallido',
  'platform.mfa_failed': 'Verificación en dos pasos de operador fallida',
  'platform.logout': 'Cierre de sesión de operador',
  'platform.refresh_reuse_detected': 'Reutilización de sesión de operador detectada',
  'platform.session_revoked': 'Sesión de operador revocada',
  'platform.operator_created': 'Operador creado',
  'platform.operator_disabled': 'Operador desactivado',
  'platform.operator_reset': 'Credenciales de operador reemplazadas',
  'platform.tenant_created': 'Despacho creado',
  'platform.tenant_status_changed': 'Estado de despacho cambiado',
  'platform.domain_added': 'Dominio de despacho registrado',
  'platform.tenant_providers_changed': 'Proveedores de despacho cambiados',
  'platform.tenant_updated': 'Datos de despacho cambiados',
  'platform.owner_invitation_reissued': 'Nuevo enlace para el propietario de un despacho',
} as const;

export type PlatformSecurityEventType = keyof typeof PLATFORM_SECURITY_EVENTS;
export const PLATFORM_SECURITY_EVENT_TYPES = Object.keys(PLATFORM_SECURITY_EVENTS) as PlatformSecurityEventType[];

/** Events that record a refused or failed attempt; every other event of the catalogs is a success. */
export const SECURITY_EVENT_FAILURES: ReadonlyArray<SecurityEventType | PlatformSecurityEventType> = [
  'auth.login_failed',
  'auth.mfa_failed',
  'auth.otp_failed',
  'auth.reauth_failed',
  'auth.password_change_failed',
  'auth.external_login_failed',
  'auth.throttle_blocked',
  'auth.refresh_reuse_detected',
  'auth.mfa_setup_blocked',
  'platform.login_failed',
  'platform.mfa_failed',
  'platform.refresh_reuse_detected',
];

export type SecurityEventResult = 'success' | 'failure';

export function securityEventResult(type: string): SecurityEventResult {
  return (SECURITY_EVENT_FAILURES as readonly string[]).includes(type) ? 'failure' : 'success';
}

/**
 * One row of `GET /admin/security/events` (permission `audit.view`). Never carries a secret, a
 * keyed hash of an identifier, an email or a full phone: `details` is an allowlist of the audit
 * entry, phones are masked and the IP keeps only its network part.
 */
export interface SecurityEvent {
  id: string;
  type: SecurityEventType;
  label: string;
  result: SecurityEventResult;
  at: string;
  actor: { type: 'USER' | 'DRIVER' | 'CUSTOMER' | 'SYSTEM' | 'PLATFORM' | 'ANONYMOUS'; id: string | null; name: string | null };
  subject: { id: string; name: string | null; phoneMasked: string | null } | null;
  ip: string | null;
  userAgent: string | null;
  details: Record<string, string | number | boolean | null | string[]>;
}

/** One row of `GET /platform/security/events` (platform operators only). */
export interface PlatformSecurityEvent {
  id: string;
  type: PlatformSecurityEventType;
  label: string;
  result: SecurityEventResult;
  at: string;
  actor: { type: 'OPERATOR' | 'ANONYMOUS' | 'SYSTEM'; id: string | null; name: string | null };
  entity: { type: string; id: string | null };
  ip: string | null;
  userAgent: string | null;
  details: Record<string, string | number | boolean | null | string[]>;
}

/** A page of events, newest first. `nextCursor` goes back as `cursor` for the next page; null = no more. */
export interface SecurityEventPage<T> {
  items: T[];
  nextCursor: string | null;
}
